
import React, { useEffect, useMemo, useRef, useState } from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChatEntityPickerModal from "./ChatEntityPickerModal";
import { ScheduleDialog } from "./ChatDialogs";
import { MAX_ATTACHMENTS } from "../../utils/chatAlbums";
import { detectMediaDocumentKind } from "../../utils/mediaDocument";

/** Микрофон: пока поле пустое, кнопка справа записывает голосовое. */
const MicIcon = () => (
    <svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true">
        <rect x="8.5" y="2.5" width="7" height="12" rx="3.5" fill="currentColor" />
        <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        <path d="M12 18v3.2M8.8 21.2h6.4" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
);

/** Отправить: появляется, как только в поле есть текст или файл. */
const SendIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M3.4 20.4 21 12 3.4 3.6l-.02 6.53L15.5 12 3.38 13.87z" fill="currentColor" />
    </svg>
);

/** Держат кнопку отправки столько — открывается её меню (как в приложении). */
const SEND_MENU_HOLD_MS = 420;

const getForwardTitle = (message) => {
    if (!message) return "";
    return message.text || message.fileName || message.entity?.title || "Пересланное сообщение";
};

const formatBytes = (value) => {
    const bytes = Number(value || 0);
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

// Вид файла — по расширению: ArtCAM, AutoCAD, CorelDRAW и т. п. браузер
// называет «картинкой», и раньше для них пыталось построиться превью-фото.
const fileKind = (file) => {
    const kind = detectMediaDocumentKind({ name: String(file?.name || ""), mimeType: String(file?.type || "") });
    if (kind === "image" || kind === "video" || kind === "audio") return kind;
    return "doc";
};

/** Мини-превью выбранного файла: фото — картинкой, остальное — значком. */
function PendingFile({ file, onRemove, disabled }) {
    const kind = fileKind(file);
    const [url, setUrl] = useState("");

    useEffect(() => {
        if (kind !== "image" && kind !== "video") return undefined;
        const next = URL.createObjectURL(file);
        setUrl(next);
        return () => URL.revokeObjectURL(next);
    }, [file, kind]);

    return (
        <div className={`chat-input-AO__pending-item is-${kind}`} title={`${file.name} · ${formatBytes(file.size)}`}>
            {kind === "image" && url ? <img src={url} alt="" draggable={false} /> : null}
            {kind === "video" && url ? <video src={url} muted preload="metadata" /> : null}
            {kind !== "image" && kind !== "video" ? (
                <span className="chat-input-AO__pending-doc">
                    <i>{kind === "audio" ? "♪" : "▤"}</i>
                    <b>{file.name}</b>
                    <small>{formatBytes(file.size)}</small>
                </span>
            ) : null}
            {kind === "video" ? <span className="chat-input-AO__pending-badge">▶</span> : null}
            <button type="button" onClick={onRemove} disabled={disabled} aria-label={`Убрать ${file.name}`}>✕</button>
        </div>
    );
}

export default function InputChatUI({
    text,
    setText,
    files = [],
    onAddFiles,
    onRemoveFile,
    onClearFiles,
    inputRef,
    officeId = null,
    forwardDraft = null,
    onCancelForward = null,
    onSend,
    onShareEntity = null,
    onVoiceFinished,
    onOpenVideoNote = null,
    scheduleEnabled = false,
    sending = false,
    sendError = null,
    attachError = "",
}) {
    const imageInputRef = useRef(null);
    const docInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const startedAtRef = useRef(0);
    const cancelledRef = useRef(false);
    /** Время отложенной отправки для голосового, которое сейчас остановим. */
    const voiceSendAtRef = useRef(null);
    const holdTimerRef = useRef(null);
    const holdFiredRef = useRef(false);
    const sendButtonRef = useRef(null);

    const [attachOpen, setAttachOpen] = useState(false);
    const [entityPickerOpen, setEntityPickerOpen] = useState(false);
    const [recording, setRecording] = useState(false);
    const [recordTime, setRecordTime] = useState(0);
    const [localError, setLocalError] = useState("");
    /** Меню кнопки отправки: где оно стоит на экране. */
    const [sendMenu, setSendMenu] = useState(null);
    /** Окно выбора времени: что отложить — набранное или записанное голосовое. */
    const [scheduleFor, setScheduleFor] = useState(null);

    const hasContent = !!(text.trim() || files.length || forwardDraft);
    const canSend = hasContent && !sending;
    const visibleError = localError || attachError || sendError || "";

    const filesMeta = useMemo(() => {
        if (!files.length) return "";
        const total = files.reduce((sum, item) => sum + Number(item.size || 0), 0);
        return `${files.length} из ${MAX_ATTACHMENTS} · ${formatBytes(total)}`;
    }, [files]);

    useEffect(() => () => {
        if (holdTimerRef.current) window.clearTimeout(holdTimerRef.current);
    }, []);

    const send = (options = {}) => {
        if (!hasContent || sending) return;
        setLocalError("");
        onSend?.({ text, files, ...options });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (holdFiredRef.current) {
            holdFiredRef.current = false;
            return;
        }
        send();
    };

    const handleKeyDown = (event) => {
        if (event.key !== "Enter" || event.shiftKey || event.nativeEvent?.isComposing) return;
        event.preventDefault();
        if (canSend) send();
    };

    const handleFiles = (event) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = "";
        setAttachOpen(false);
        if (!selected.length) return;
        setLocalError("");
        onAddFiles?.(selected);
    };

    // Фото из буфера обмена (Ctrl+V скриншота) — сразу во вложения.
    const handlePaste = (event) => {
        const pasted = Array.from(event.clipboardData?.files || []);
        if (!pasted.length) return;
        event.preventDefault();
        onAddFiles?.(pasted);
    };

    const clearRecordTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const stopStream = () => {
        if (!streamRef.current) return;
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
    };

    const startRecord = async () => {
        if (recording || sending) return;

        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setLocalError("Этот браузер не поддерживает запись голосовых сообщений.");
            return;
        }

        try {
            setLocalError("");
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            streamRef.current = stream;
            chunksRef.current = [];
            cancelledRef.current = false;
            voiceSendAtRef.current = null;
            startedAtRef.current = Date.now();

            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (event) => {
                if (event.data?.size) chunksRef.current.push(event.data);
            };

            recorder.onstop = () => {
                clearRecordTimer();
                stopStream();

                const durationMs = Date.now() - startedAtRef.current;
                const mimeType = recorder.mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type: mimeType });
                const sendAt = voiceSendAtRef.current;

                setRecording(false);
                setRecordTime(0);
                chunksRef.current = [];
                mediaRecorderRef.current = null;
                voiceSendAtRef.current = null;

                if (!cancelledRef.current && blob.size > 0) {
                    onVoiceFinished?.({ blob, durationMs, ...(sendAt ? { sendAt } : {}) });
                }
            };

            recorder.start();
            setRecording(true);
            setRecordTime(0);

            timerRef.current = setInterval(() => {
                setRecordTime(Math.floor((Date.now() - startedAtRef.current) / 1000));
            }, 300);
        } catch (_error) {
            setRecording(false);
            clearRecordTimer();
            stopStream();
            setLocalError("Нет доступа к микрофону. Проверьте разрешение браузера.");
        }
    };

    const stopRecord = (sendAt = null) => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;
        cancelledRef.current = false;
        voiceSendAtRef.current = sendAt;
        mediaRecorderRef.current.stop();
    };

    const cancelRecord = () => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;
        cancelledRef.current = true;
        mediaRecorderRef.current.stop();
    };

    const formatRecordTime = () => {
        const min = Math.floor(recordTime / 60);
        const sec = recordTime % 60;
        return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };

    // ---------- меню кнопки отправки ----------

    /** Правая кнопка или долгое нажатие по «Отправить» — меню над кнопкой. */
    const openSendMenu = () => {
        if (sending || forwardDraft || !menuItems.length) return;
        const rect = sendButtonRef.current?.getBoundingClientRect?.();
        if (!rect) return;
        setAttachOpen(false);
        setSendMenu({ right: Math.max(8, window.innerWidth - rect.right), bottom: Math.max(8, window.innerHeight - rect.top + 8) });
    };

    const startHold = (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        holdFiredRef.current = false;
        if (holdTimerRef.current) window.clearTimeout(holdTimerRef.current);
        holdTimerRef.current = window.setTimeout(() => {
            holdFiredRef.current = true;
            holdTimerRef.current = null;
            openSendMenu();
        }, SEND_MENU_HOLD_MS);
    };

    const endHold = () => {
        if (holdTimerRef.current) {
            window.clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
    };

    const sendButtonHandlers = {
        onContextMenu: (event) => {
            event.preventDefault();
            endHold();
            openSendMenu();
        },
        onPointerDown: startHold,
        onPointerUp: endHold,
        onPointerLeave: endHold,
        onPointerCancel: endHold,
    };

    // Голосовое записывает сама кнопка (пока поле пустое), поэтому в меню его нет.
    const menuItems = [
        ...(scheduleEnabled && (recording || hasContent) ? [{
            key: "later",
            icon: "◷",
            label: recording ? "Отправить голосовое позже" : "Отправить позже",
            run: () => setScheduleFor(recording ? "voice" : "draft"),
        }] : []),
        ...(!recording && onOpenVideoNote ? [{
            key: "note",
            icon: "◉",
            label: "Записать кружок",
            run: () => onOpenVideoNote(),
        }] : []),
    ];

    const sendMenuNode = sendMenu ? (
        <>
            <button type="button" className="chat-send-menu-AO__backdrop" onClick={() => setSendMenu(null)} aria-label="Закрыть меню" />
            <div
                className="chat-send-menu-AO"
                style={{ right: sendMenu.right, bottom: sendMenu.bottom }}
                role="menu"
                onContextMenu={(event) => event.preventDefault()}
            >
                {menuItems.map((item, index) => (
                    <button
                        key={item.key}
                        type="button"
                        role="menuitem"
                        className="chat-send-menu-AO__item"
                        style={{ animationDelay: `${(menuItems.length - 1 - index) * 45}ms` }}
                        disabled={item.disabled}
                        title={item.hint || item.label}
                        onClick={() => {
                            setSendMenu(null);
                            item.run();
                        }}
                    >
                        <span>{item.label}</span>
                        <i>{item.icon}</i>
                    </button>
                ))}
            </div>
        </>
    ) : null;

    const scheduleNode = scheduleFor ? (
        <ScheduleDialog
            title={scheduleFor === "voice" ? "Отправить голосовое позже" : "Отправить позже"}
            onClose={() => setScheduleFor(null)}
            onConfirm={(sendAt) => {
                const target = scheduleFor;
                setScheduleFor(null);
                if (target === "voice") stopRecord(sendAt);
                else send({ sendAt });
            }}
        />
    ) : null;

    if (recording) {
        return (
            <div className="chat-input-AO chat-input-AO--recording">
                <button type="button" className="chat-input-AO__cancel" onClick={cancelRecord}>Отмена</button>
                <div className="chat-input-AO__recording-state">
                    <span className="chat-input-AO__pulse" />
                    <strong>Запись</strong>
                    <span>{formatRecordTime()}</span>
                </div>
                <button
                    type="button"
                    ref={sendButtonRef}
                    className="chat-input-AO__send"
                    onClick={() => {
                        if (holdFiredRef.current) {
                            holdFiredRef.current = false;
                            return;
                        }
                        stopRecord();
                    }}
                    title={scheduleEnabled ? "Отправить голосовое (правая кнопка — отправить позже)" : "Отправить голосовое"}
                    aria-label="Отправить голосовое"
                    {...sendButtonHandlers}
                >
                    <SendIcon />
                </button>
                {sendMenuNode}
                {scheduleNode}
            </div>
        );
    }

    return (
        <form className="chat-input-AO" onSubmit={handleSubmit}>
            <div className="chat-input-AO__composer">
                {forwardDraft && (
                    <div className="chat-input-AO__forward-draft">
                        <div>
                            <b>Пересылка</b>
                            <span>{getForwardTitle(forwardDraft)}</span>
                        </div>
                        <button type="button" onClick={onCancelForward} aria-label="Отменить пересылку">✕</button>
                    </div>
                )}

                {files.length > 0 && (
                    <div className="chat-input-AO__pending">
                        <div className="chat-input-AO__pending-head">
                            <b>Вложения</b>
                            <small>{filesMeta}</small>
                            <button type="button" onClick={onClearFiles} disabled={sending}>Убрать все</button>
                        </div>
                        <div className="chat-input-AO__pending-list">
                            {files.map((item, index) => (
                                <PendingFile
                                    key={`${item.name}-${item.size}-${item.lastModified}-${index}`}
                                    file={item}
                                    disabled={sending}
                                    onRemove={() => onRemoveFile?.(index)}
                                />
                            ))}
                            {files.length < MAX_ATTACHMENTS ? (
                                <button
                                    type="button"
                                    className="chat-input-AO__pending-add"
                                    onClick={() => imageInputRef.current?.click()}
                                    title="Добавить ещё"
                                >
                                    +
                                </button>
                            ) : null}
                        </div>
                    </div>
                )}

                <div className="chat-input-AO__row">
                    <button
                        type="button"
                        className="chat-input-AO__icon"
                        onClick={() => setAttachOpen((state) => !state)}
                        title="Прикрепить"
                        disabled={sending}
                    >
                        <AttachFileIcon sx={{ fontSize: 20 }} />
                    </button>

                    <textarea
                        ref={inputRef}
                        value={text}
                        rows={1}
                        placeholder={forwardDraft ? "Добавьте комментарий…" : files.length ? "Подпись…" : "Сообщение…"}
                        onChange={(event) => setText(event.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        disabled={sending}
                        aria-label="Сообщение"
                    />

                    {/*
                      Одна кнопка справа, как в Telegram: поле пустое — микрофон
                      (нажали — пошла запись голосового), появился текст или файл —
                      «Отправить». Правая кнопка или удержание — меню: «Отправить
                      позже» и «Записать кружок».
                    */}
                    <button
                        type={hasContent ? "submit" : "button"}
                        ref={sendButtonRef}
                        className={`chat-input-AO__send ${hasContent ? "is-send" : "is-mic"}`}
                        disabled={sending}
                        onClick={hasContent ? undefined : () => {
                            if (holdFiredRef.current) {
                                holdFiredRef.current = false;
                                return;
                            }
                            startRecord();
                        }}
                        title={hasContent
                            ? (scheduleEnabled ? "Отправить. Правая кнопка или удержание — отправить позже" : "Отправить")
                            : (onOpenVideoNote ? "Записать голосовое. Правая кнопка или удержание — кружок" : "Записать голосовое")}
                        aria-label={hasContent ? "Отправить" : "Записать голосовое"}
                        {...sendButtonHandlers}
                    >
                        {sending ? (
                            <span className="chat-input-AO__spinner" />
                        ) : (
                            <span key={hasContent ? "send" : "mic"} className="chat-input-AO__send-icon">
                                {hasContent ? <SendIcon /> : <MicIcon />}
                            </span>
                        )}
                    </button>
                </div>

                {visibleError && <div className="chat-input-AO__error" role="alert">{visibleError}</div>}
            </div>

            <input ref={imageInputRef} type="file" accept="image/*,video/*" multiple hidden onChange={handleFiles} />
            {/* Документы — любые файлы: ArtCAM, AutoCAD, Blender, CorelDRAW и т. д. */}
            <input ref={docInputRef} type="file" multiple hidden onChange={handleFiles} />

            {attachOpen && (
                <>
                    <button type="button" className="chat-input-AO__backdrop" onClick={() => setAttachOpen(false)} aria-label="Закрыть меню вложений" />
                    <div className="chat-input-AO__attach">
                        <button type="button" onClick={() => imageInputRef.current?.click()}>
                            <span>▧</span>
                            <div><b>Фото или видео</b><small>До {MAX_ATTACHMENTS} штук за раз · фото уйдут альбомом</small></div>
                        </button>
                        <button type="button" onClick={() => docInputRef.current?.click()}>
                            <span>▤</span>
                            <div><b>Файлы</b><small>До {MAX_ATTACHMENTS} штук за раз, любые форматы до 50 МБ</small></div>
                        </button>
                        {onOpenVideoNote ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setAttachOpen(false);
                                    onOpenVideoNote();
                                }}
                            >
                                <span>◉</span>
                                <div><b>Кружок</b><small>Видеосообщение с камеры, до минуты</small></div>
                            </button>
                        ) : null}

                        {onShareEntity ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setAttachOpen(false);
                                    setEntityPickerOpen(true);
                                }}
                            >
                                <span>◆</span>
                                <div>
                                    <b>Товар, заказ или объект</b>
                                    <small>Каталог сайта, заказы, задания, сотрудники, склад, 3D-сессии</small>
                                </div>
                            </button>
                        ) : null}
                    </div>
                </>
            )}

            {sendMenuNode}
            {scheduleNode}

            <ChatEntityPickerModal
                open={entityPickerOpen}
                officeId={officeId}
                sending={sending}
                onClose={() => setEntityPickerOpen(false)}
                onSelect={onShareEntity}
            />
        </form>
    );
}
